import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { CreateCommitteeDto, committeesApi, handleApiError } from '../../services/committeesService';

interface AddCommitteeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: number;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  margin: 0;
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const GeneralError = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--error-color);
  font-size: 14px;
`;

const InfoBox = styled.div`
  background: #f0f7ff;
  border: 1px solid #008EA2;
  border-radius: var(--border-radius-md);
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  color: var(--text-primary);
`;

const WarningBox = styled.div`
  background: #fff4e6;
  border: 1px solid #d97706;
  border-radius: var(--border-radius-md);
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const WarningIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--warning-color);
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'secondary' ? 'var(--border-color)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddCommitteeModal: React.FC<AddCommitteeModalProps> = ({ isOpen, onClose, onSuccess, clientId }) => {
  const [loading, setLoading] = useState(false);
  const [loadingCommittees, setLoadingCommittees] = useState(false);
  const [evaluationNumber, setEvaluationNumber] = useState<number>(1);
  const [formData, setFormData] = useState<CreateCommitteeDto>({
    clientId,
    committeeDate: '',
    location: '',
    escortNeeded: false,
    escortType: '',
    notes: '',
    usesLawyer: false,
    lawyerName: '',
    requiresMedicalOpinion: false,
    medicalOpinionDetails: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [generalError, setGeneralError] = useState<string>('');

  // Load existing committees to determine evaluation number
  useEffect(() => {
    const loadCommittees = async () => {
      if (!isOpen) return;

      setLoadingCommittees(true);
      try {
        const committees = await committeesApi.getByClientId(clientId);
        // Count ALL committees (not just evaluated ones) to determine the next evaluation number
        setEvaluationNumber(committees.length + 1);
      } catch (error) {
        console.error('Error loading committees:', error);
        setEvaluationNumber(1);
      } finally {
        setLoadingCommittees(false);
      }
    };

    loadCommittees();
  }, [isOpen, clientId]);

  // Validate a single field (for onBlur validation)
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'committeeDate':
        if (!value) {
          return 'יש להזין תאריך ושעה';
        }
        break;

      case 'escortType':
        if (formData.escortNeeded && !value) {
          return 'יש לציין סוג ליווי';
        }
        break;

      default:
        return '';
    }
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    const committeeDateError = validateField('committeeDate', formData.committeeDate);
    if (committeeDateError) newErrors.committeeDate = committeeDateError;

    const escortTypeError = validateField('escortType', formData.escortType);
    if (escortTypeError) newErrors.escortType = escortTypeError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field: string, value: any) => {
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      // Clear error if field is now valid
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setGeneralError(''); // Clear previous errors
    try {
      await committeesApi.create(formData);
      onSuccess();
      onClose();
      // Reset form
      setFormData({
        clientId,
        committeeDate: '',
        location: '',
        escortNeeded: false,
        escortType: '',
        notes: '',
        usesLawyer: false,
        lawyerName: '',
        requiresMedicalOpinion: false,
        medicalOpinionDetails: ''
      });
      setErrors({});
      setGeneralError('');
    } catch (error) {
      console.error('Error creating committee:', error);
      setGeneralError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>הוספת ועדה חדשה</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {generalError && (
              <GeneralError>{generalError}</GeneralError>
            )}

            {loadingCommittees ? (
              <InfoBox>טוען מידע...</InfoBox>
            ) : evaluationNumber === 3 ? (
              <WarningBox>
                <WarningIcon><AlertTriangle size={18} strokeWidth={2} /></WarningIcon>
                <span>זוהי הערכה שלישית ואחרונה! לפי כללי ביטוח לאומי, לא ניתן להגיש יותר מ-3 הערכות לתביעה אחת.</span>
              </WarningBox>
            ) : evaluationNumber > 3 ? (
              <WarningBox>
                <WarningIcon><AlertTriangle size={18} strokeWidth={2} /></WarningIcon>
                <span>שגיאה: הגעת למקסימום 3 הערכות לתביעה זו. לא ניתן להוסיף ועדה נוספת.</span>
              </WarningBox>
            ) : (
              <InfoBox>
                מספר הערכה: {evaluationNumber} מתוך 3
              </InfoBox>
            )}

            <FormGroup>
              <Label>תאריך ושעת הועדה *</Label>
              <Input
                type="datetime-local"
                value={formData.committeeDate}
                onChange={(e) => {
                  setFormData({ ...formData, committeeDate: e.target.value });
                  if (errors.committeeDate) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.committeeDate;
                      return newErrors;
                    });
                  }
                }}
                onBlur={(e) => handleBlur('committeeDate', e.target.value)}
                hasError={!!errors.committeeDate}
                required
              />
              {errors.committeeDate && <ErrorMessage>{errors.committeeDate}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>מיקום (סניף ביטוח לאומי)</Label>
              <Input
                type="text"
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="למשל: סניף ירושלים"
              />
            </FormGroup>

            <FormGroup>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={formData.escortNeeded}
                  onChange={(e) => setFormData({ ...formData, escortNeeded: e.target.checked })}
                />
                <Label style={{ margin: 0 }}>נדרש ליווי</Label>
              </CheckboxContainer>
            </FormGroup>

            {formData.escortNeeded && (
              <FormGroup>
                <Label>סוג ליווי *</Label>
                <Input
                  type="text"
                  value={formData.escortType || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, escortType: e.target.value });
                    if (errors.escortType) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.escortType;
                        return newErrors;
                      });
                    }
                  }}
                  onBlur={(e) => handleBlur('escortType', e.target.value)}
                  placeholder="משפחתי / מקצועי"
                  hasError={!!errors.escortType}
                />
                {errors.escortType && <ErrorMessage>{errors.escortType}</ErrorMessage>}
              </FormGroup>
            )}

            <FormGroup>
              <Label>הערות</Label>
              <TextArea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="הערות נוספות..."
              />
            </FormGroup>

            <FormGroup>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={formData.usesLawyer}
                  onChange={(e) => setFormData({ ...formData, usesLawyer: e.target.checked })}
                />
                <Label style={{ margin: 0 }}>נעזר בעורך דין</Label>
              </CheckboxContainer>
            </FormGroup>

            {formData.usesLawyer && (
              <FormGroup>
                <Label>שם עורך הדין</Label>
                <Input
                  type="text"
                  value={formData.lawyerName || ''}
                  onChange={(e) => setFormData({ ...formData, lawyerName: e.target.value })}
                  placeholder="שם מלא"
                />
              </FormGroup>
            )}

            <FormGroup>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={formData.requiresMedicalOpinion}
                  onChange={(e) => setFormData({ ...formData, requiresMedicalOpinion: e.target.checked })}
                />
                <Label style={{ margin: 0 }}>נדרשת חוות דעת רפואית</Label>
              </CheckboxContainer>
            </FormGroup>

            {formData.requiresMedicalOpinion && (
              <FormGroup>
                <Label>פרטי חוות הדעת</Label>
                <TextArea
                  value={formData.medicalOpinionDetails || ''}
                  onChange={(e) => setFormData({ ...formData, medicalOpinionDetails: e.target.value })}
                  placeholder="תיאור חוות הדעת הנדרשת..."
                />
              </FormGroup>
            )}
          </ModalBody>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={onClose}>
              ביטול
            </Button>
            <Button type="submit" disabled={loading || evaluationNumber > 3}>
              {loading ? 'שומר...' : evaluationNumber > 3 ? 'הגעת למקסימום' : 'שמירה'}
            </Button>
          </ButtonContainer>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddCommitteeModal;
